import React, {useEffect, useState, MouseEvent} from "react";
import {MenuItem, MenuItemCombination, MenuSection} from "../../../@types/stores/menu";
import menuStore from "../../../stores/menu";
import {useNavigate} from "react-router";
import {moveToTop} from "../../../helpers/DomHelper";
import defaultImg from "../../../assets/images/default.webp";
import {LazyLoadImage} from 'react-lazy-load-image-component';
import {
    addToLog,
    getAllergenById,
    getCurrency, getCurrentCategory, getCurrentSubCategory,
    getHost,
    getPreferenceById, isArticleHasPreferences,
    isMenuItem,
    isMenuItemCombination
} from "../../../helpers/ApiHelper";
import {ArticleViewType} from "../../../@types/enums/ArticleViewType";
import {ReactComponent as IconInfo} from "../../../assets/icons/info.svg";
import orderStore from "../../../stores/order";
import {observer} from "mobx-react";
import {hideLoader, showLoader} from "../../../helpers/ComponentHelper";
import {v4 as guid} from 'uuid';
import {useTranslation} from "react-i18next";
import useLayoutStyles from "../../../hooks/styles";
import ImgHalfNHalf from "../../../assets/images/halfNhalf.jpg";
import useHasHalfAndHalf from "../../../hooks/halfAndHalf";


export interface SubCategoryMenuProps {
    category: number;
    subCategory: MenuSection;
}

const SubCategoryMenu = (props: SubCategoryMenuProps) => {
    const [t] = useTranslation();
    const navigate = useNavigate();
    const styles = useLayoutStyles();
    const hasHalfAndHalf = useHasHalfAndHalf(props.subCategory);

    const [subCategory, setSubCategories] = useState<MenuSection | undefined>(undefined);

    const goTo = (item: MenuItem | MenuItemCombination, subCategoryId: number) => {
        menuStore.changeSubCategory(subCategoryId);

        if (isMenuItem(item)) {
            // ARTICLE
            item = item as MenuItem;

            const category = getCurrentCategory();
            const subCategory = getCurrentSubCategory();
            addToLog(`Click on article #${item.id} / ${item.number} / ${item.name}, category #${category?.id || "<undefined>"} / ${category?.name || "<undefined>"}, subcategory #${subCategory?.id} / ${subCategory?.name || "<undefined>"}`);

            // ONE_PAGE
            if (!localStorage.getItem("articleViewType") || localStorage.getItem("articleViewType") === ArticleViewType.ONE_PAGE) {
                moveToTop();
                navigate(`/menu/${menuStore.navigation.category}/${subCategoryId}/${item.number}`);
            }

            // GROUP_PAGE
            else if (localStorage.getItem("articleViewType") === ArticleViewType.GROUP_PAGE) {
                if (item.additionalSellUnits?.length) {
                    moveToTop();
                    navigate(`/menu/${menuStore.navigation.category}/${subCategoryId}/${item.number}/additionalSellUnits`);
                    return;
                }
                else if (item.modifierGroups?.length) {
                    moveToTop();
                    navigate(`/menu/${menuStore.navigation.category}/${subCategoryId}/${item.number}/modifiers`);
                    return;
                } else if (item.addonGroups?.length) {
                    moveToTop();
                    navigate(`/menu/${menuStore.navigation.category}/${subCategoryId}/${item.number}/addons`);
                    return;
                } else if (item.ingredientGroups?.length) {
                    moveToTop();
                    navigate(`/menu/${menuStore.navigation.category}/${subCategoryId}/${item.number}/ingredients`);
                    return;
                } else {
                    addToOrder(item);
                    return;
                }
            }

            // INDIVIDUAL_PAGE
            else if (localStorage.getItem("articleViewType") === ArticleViewType.INDIVIDUAL_PAGE) {
                if (item.additionalSellUnits?.length) {
                    moveToTop();
                    navigate(`/menu/${menuStore.navigation.category}/${subCategoryId}/${item.number}/additionalSellUnits`);
                    return;
                } else if (item.modifierGroups?.length) {
                    moveToTop();
                    navigate(`/menu/${menuStore.navigation.category}/${subCategoryId}/${item.number}/modifiers/${item.modifierGroups.at(0)?.id}`);
                    return;
                } else if (item.addonGroups?.length) {
                    moveToTop();
                    navigate(`/menu/${menuStore.navigation.category}/${subCategoryId}/${item.number}/addons/${item.addonGroups.at(0)?.id}`);
                    return;
                } else if (item.ingredientGroups?.length) {
                    moveToTop();
                    navigate(`/menu/${menuStore.navigation.category}/${subCategoryId}/${item.number}/ingredients`);
                    return;
                } else {
                    addToOrder(item);
                    return;
                }
            }

            // Open info block
            menuStore.changeMenuItem(item.id);
        }
        else {
            //COMBINATION
            item = item as MenuItemCombination;

            const category = getCurrentCategory();
            const subCategory = getCurrentSubCategory();
            addToLog(`Click on combination #${item.id} / ${item.name}, category #${category?.id || "<undefined>"} / ${category?.name || "<undefined>"}, subcategory #${subCategory?.id} / ${subCategory?.name || "<undefined>"}`);


            moveToTop();
            navigate(`/menu/combination/${menuStore.navigation.category}/${subCategoryId}/${item.id}`);
        }
    }

    const addToOrder = async (item: MenuItem) => {
        showLoader();
        const r = await orderStore.addToOrder({
            id: guid(),
            menuId: menuStore.navigation.category,
            subMenuId: menuStore.navigation.subCategory,
            name: item.name,
            number: item.number,
            count: 1,
            price: item.price,
            total: item.price,
            img: item.previewList
        });
        if (r) {
            navigate("/recommendations");
        }
        hideLoader();
    }

    const openInfo = (e: MouseEvent<SVGSVGElement>, item: MenuItem | MenuItemCombination) => {
        e.stopPropagation();
        menuStore.changeMenuItem(item.id);
    }

    useEffect(() => {
        setSubCategories(menuStore.menu?.find(x => x.id === props.category)?.submenu?.find(x => x.id === props.subCategory.id));
    }, [props.subCategory]);

    const isSubCategoryHasArticles = (subCategory: MenuSection | undefined): boolean => {
        if (!subCategory?.articles) return false;

        return subCategory.articles.filter(x => isArticleHasPreferences(x)).length > 0;
    }

    const goToHalfAndHalf = (categoryId: number, subCategoryId: number) => {
        navigate(`/menu/halfAndHalf/${categoryId}/${subCategoryId}`);
    }

    if (!isSubCategoryHasArticles(subCategory)) return null;

    return <div className="SubCategoryMenu">
        {subCategory
            ? <div>
                <h2>{subCategory.name}</h2>
                <div className="items" x-filter-allergens={JSON.stringify(menuStore.filter.allergens)}>
                    {hasHalfAndHalf
                        ?  <div key={`menuItem_${subCategory.name}`} className="item" onClick={() => {goToHalfAndHalf(props.category, props.subCategory.id)}}>
                            <div className={`img`}>
                                <LazyLoadImage alt={t("halfNHalf.title")} src={ImgHalfNHalf} effect="blur"/>
                            </div>
                            <div className="text">
                                <div className="title">{t("halfNHalf.title")}</div>
                            </div>
                        </div>
                        : null
                    }

                    {subCategory.articles?.filter(x => isArticleHasPreferences(x)).map(x =>
                        <div key={`menuItem_${x.name}`} className="item" onClick={() => goTo(x, subCategory.id)}>
                            <div className="tagsImages">
                                {x.allergens?.filter(y => menuStore.filter.allergens.indexOf(y) !== -1).map(y => getAllergenById(y)?.image ?
                                    <img key={`allergenTagImage_${y}`}
                                         src={`${getHost()}${getAllergenById(y)?.image || ""}`}
                                         alt={getAllergenById(y)?.name}/> : null)}
                                {x.preferences?.filter(y => menuStore.filter.preferences.indexOf(y) !== -1).map(y => getPreferenceById(y)?.image ?
                                    <img key={`preferenceTagImage_${y}`}
                                         src={`${getHost()}${getPreferenceById(y)?.image || ""}`}
                                         alt={getPreferenceById(y)?.name}/> : null)}
                            </div>
                            <div
                                className={`img ${x.allergens?.filter(y => menuStore.filter.allergens.indexOf(y) !== -1).length ? "blur" : ""}`}>
                                <LazyLoadImage alt={x.name}
                                               src={x.previewList ? `${getHost()}${x.previewList}` : (styles?.article.defaultListImage ? `${getHost()}${styles.article.defaultListImage}` : defaultImg)}
                                               effect="blur"/>
                            </div>
                            <div className="text">
                                <div className="tagContainer">
                                    <div className="tags">
                                        {x.allergens?.filter(y => menuStore.filter.allergens.indexOf(y) !== -1).map(y => getAllergenById(y) ?
                                            <div key={`allergenTag_${y}`}
                                                 className="tag allergen">{getAllergenById(y)?.name || ""}</div> : null)}
                                        {x.preferences?.filter(y => menuStore.filter.preferences.indexOf(y) !== -1).map(y => getPreferenceById(y) ?
                                            <div key={`preferenceTag_${y}`}
                                                 className="tag preference">{getPreferenceById(y)?.name || ""}</div> : null)}
                                    </div>
                                </div>
                                <div className="title">
                                    {x.name}
                                </div>
                                <div className="itemFooter">
                                    {!localStorage.getItem("articleViewType") || localStorage.getItem("articleViewType") === ArticleViewType.ONE_PAGE
                                        ? <div/>
                                        : <IconInfo onClick={(e) => openInfo(e, x)}/>
                                    }
                                    <div className="price">
                                        {isMenuItem(x)
                                            ? `${x.price?.toFixed(2) || "0.00"} ${getCurrency()}`
                                            : null
                                        }
                                        {isMenuItemCombination(x)
                                            ? `${(x as MenuItemCombination).price ? "" : t("minPrice")} ${(x as MenuItemCombination).price?.toFixed(2) || (x as MenuItemCombination).minPrice?.toFixed(2) ||  "0.00"} ${getCurrency()}`
                                            : null
                                        }
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            : null
        }
    </div>
}

export default observer(SubCategoryMenu);
